import React from "react";
import { I18n } from "@aws-amplify/core";
import { connect } from "react-redux";
import { get, map, size, forEach } from "lodash";

import { fetchPost } from "../../api/fetch";
import CsvExportDialog from "../Common/CsvExportDialog";
import { eventGroupsData } from "../../constants/eventGroups";
import { human } from "../../constants/common";
import { driveInOut, walkInOut } from "../../constants/reducers";
import {
  extractDriveInOutInfo,
  extractWalkInOutInfo,
  getDacLov,
  addChildEventGroups,
  getReferenceSurvey,
} from "../../utils/eventGroups";
import {
  getThreshold,
  getTemperatureByOperation,
} from "../../utils/temperature";
import { extractValuesFromData } from "../../utils/filters";
import { displayDataCsv } from "../../utils/data";
import { fetchChildEventGroups } from "../../actions/siteEntranceExit/tabs";

const SiteLogCsvExport = (props) => {
  const { section, columns, language } = props;

  const fetchData = async (filters) => {
    try {
      const tab = get(props, ["siteEntranceExit", section, "tabs", 0]);
      let body = {
        filter2: {
          log: {
            zones: {
              operation: "==",
              values: tab.zoneIds,
            },
            activity_types: {
              operation: "==",
              values: tab.logTypes,
            },
          },
        },
        pagination: {
          offset: 0,
        },
        data: eventGroupsData,
      };

      if (get(filters, ["startDate"]) && get(filters, ["endDate"])) {
        body.filter2.log.processed_on = {
          operation: "between",
          values: [get(filters, ["startDate"]), get(filters, ["endDate"])],
        };
      }
      let lov = await getDacLov(section);
      let eventGroups = [];
      let values = {};
      let data = [];
      let total = 1;
      while (size(data) < total) {
        body.pagination.offset = size(data);
        let res = await fetchPost("new/event_groups", body);
        data = [...data, ...res.items];
        total = res.total;
      }
      let childEventGroups = await fetchChildEventGroups(
        section,
        map(data, (item) => item.id)
      );

      for (let i = 0; i < size(data); i++) {
        let group;
        if (section === driveInOut) {
          group = extractDriveInOutInfo(
            data[i],
            () => ({ user: { language } }),
            lov
          );
        } else {
          group = extractWalkInOutInfo(
            data[i],
            () => ({ user: { language } }),
            lov
          );
        }

        group = addChildEventGroups(
          section,
          group,
          childEventGroups.filter((item) => item.parent.id === group.id),
          lov,
          language
        );
        if (get(group, [human, "verification", 0, "surveyIdToFetch"])) {
          group[human].verification[0] = await getReferenceSurvey(
            get(group, [human, "verification", 0, "surveyIdToFetch"]),
            lov,
            language,
            get(group, [human, "verification", 0, "instruction"]),
            get(group, [human, "verification", 0, "instructionColor"])
          );
        }
        eventGroups.push(group);
        values = await extractValuesFromData(values, group);
      }
      return eventGroups;
    } catch (e) {
      console.log(e);
    }
  };

  const title = I18n.get(`site_${section}`);

  let dataFunctions = [];
  // Summary
  dataFunctions.push((item) => get(item, ["complete", "id"]));
  if (section === driveInOut) {
    dataFunctions.push((item) =>
      get(item, ["main", "tailgating"]) === true
        ? I18n.get("profiles_yes")
        : get(item, ["main", "tailgating"]) === false
          ? I18n.get("profiles_no")
          : "-"
    );
  }
  dataFunctions.push((item) => displayDataCsv(get(item, ["main", "dateTime"])));
  dataFunctions.push((item) => displayDataCsv(get(item, ["main", "zone"])));
  dataFunctions.push((item) => displayDataCsv(get(item, ["main", "status"])));
  dataFunctions.push((item) => displayDataCsv(get(item, ["main", "comments"])));
  dataFunctions.push((item) =>
    displayDataCsv(get(item, ["main", "riskLevel"]))
  );
  dataFunctions.push((item) => displayDataCsv(get(item, ["main", "logType"])));
  dataFunctions.push((item) =>
    displayDataCsv(get(item, ["main", "entryStatus"]))
  );
  dataFunctions.push((item) =>
    displayDataCsv(get(item, ["main", "processedBy"]))
  );
  dataFunctions.push((item) =>
    displayDataCsv(get(item, ["main", "modifiedOn"]))
  );
  if (section === driveInOut) {
    dataFunctions.push((item) =>
      displayDataCsv(get(item, ["main", "carrier"]))
    );
  }
  dataFunctions.push((item) =>
    get(item, ["main", "active"]) === true
      ? I18n.get("profiles_yes")
      : get(item, ["main", "active"]) === false
        ? I18n.get("profiles_no")
        : "-"
  );

  // Human
  dataFunctions.push((item) =>
    displayDataCsv(
      get(item, [section === driveInOut ? "driver" : "human", "name"])
    )
  );
  dataFunctions.push((item) =>
    displayDataCsv(
      get(item, [section === driveInOut ? "driver" : "human", "tag"])
    )
  );
  dataFunctions.push((item) =>
    displayDataCsv(
      get(item, [section === driveInOut ? "driver" : "human", "threatLevel"])
    )
  );
  if (section === walkInOut) {
    dataFunctions.push((item) =>
      displayDataCsv(
        get(item, [
          "human",
          "verification",
          0,
          "survey",
          "type",
          `name_${language}`,
        ])
      )
    );
    dataFunctions.push((item) =>
      displayDataCsv(get(item, ["human", "verification", 0, "instruction"]))
    );
    dataFunctions.push((item) =>
      get(item, ["human", "wearingMask"]) === true
        ? I18n.get("profiles_yes")
        : get(item, ["human", "wearingMask"]) === false
          ? I18n.get("profiles_no")
          : "-"
    );
    dataFunctions.push((item) => {
      const maxTemperature = getTemperatureByOperation(
        get(item, ["measuredTemperature"]),
        "max"
      );
      const threshold = getThreshold(get(item, ["measuredTemperature"]));
      return maxTemperature < threshold
        ? I18n.get("profiles_no")
        : I18n.get("profiles_yes");
    });
  }

  // Truck and trailers
  if (section === driveInOut) {
    dataFunctions.push((item) =>
      displayDataCsv(get(item, ["truck", "private"]))
    );
    forEach(
      ["truck", "trailer1", "trailer2", "container1", "container2"],
      (type) => {
        dataFunctions.push((item) => displayDataCsv(get(item, [type, "name"])));
        dataFunctions.push((item) =>
          displayDataCsv(get(item, [type, "loaded"]))
        );
        dataFunctions.push((item) => displayDataCsv(get(item, [type, "po"])));
        dataFunctions.push((item) => displayDataCsv(get(item, [type, "bol"])));
        dataFunctions.push((item) =>
          displayDataCsv(get(item, [type, "product"]))
        );
      }
    );
  }

  let headers = [
    {
      name: "site_main",
      spanNb: section === driveInOut ? 13 : 11,
    },
    {
      name: "site_driver",
      spanNb: section === driveInOut ? 3 : 7,
    },
  ];
  if (section === driveInOut) {
    headers.push(
      {
        name: "site_truck",
        spanNb: 6,
      },
      {
        name: "site_trailer1",
        spanNb: 5,
      },
      {
        name: "site_trailer2",
        spanNb: 5,
      },
      {
        name: "site_container1",
        spanNb: 5,
      },
      {
        name: "site_container2",
        spanNb: 5,
      }
    );
  }

  return (
    <CsvExportDialog
      title={title}
      columns={columns}
      fetchData={fetchData}
      dataFunctions={dataFunctions}
      style={props.style}
      maxPeriod={"7d"}
      headers={headers}
    />
  );
};

const mapStateToProps = (state) => {
  return {
    siteEntranceExit: state.siteEntranceExit,
    language: state.user.language,
  };
};

export default connect(mapStateToProps)(SiteLogCsvExport);
